import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Store, Plus, Loader2, Copy, CalendarClock, ShoppingCart, ClipboardList } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString("es-CO")}`;
const ESTADO_BADGE = {
  registro_pendiente: "bg-yellow-100 text-yellow-800", prospecto: "bg-gray-100 text-gray-700",
  aprobado: "bg-blue-100 text-blue-800", activo: "bg-green-100 text-green-800",
  suspendido: "bg-red-100 text-red-700", inactivo: "bg-gray-200 text-gray-600",
};

export default function VendedorNegocios() {
  const { user } = useAuth();
  const [negocios, setNegocios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegistro, setShowRegistro] = useState(false);
  const [showVisita, setShowVisita] = useState(null);
  const [showPedido, setShowPedido] = useState(null);
  const [catalogo, setCatalogo] = useState(null);
  const [cantidades, setCantidades] = useState({});
  const [fechaEntrega, setFechaEntrega] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({ nombre_comercial: "", responsable: "", telefono: "", email: "", tipo_establecimiento: "Tienda de barrio", direccion: "", barrio: "", ciudad: "Bogotá", acepta_terminos: true });
  const [visita, setVisita] = useState({ observaciones: "", proxima_visita: "" });

  const load = () => api.get("/b2b/vendedor/negocios").then((r) => { setNegocios(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const copiarLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/negocios?ref=${user?.id}`);
    toast.success("Link de registro copiado: compártelo con el negocio");
  };

  const registrar = async () => {
    setGuardando(true);
    try {
      await api.post("/b2b/vendedor/negocios", form);
      toast.success("Negocio registrado. Administración lo revisará para activarlo.");
      setShowRegistro(false);
      setForm({ ...form, nombre_comercial: "", responsable: "", telefono: "", email: "", direccion: "", barrio: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error registrando"); } finally { setGuardando(false); }
  };

  const registrarVisita = async () => {
    setGuardando(true);
    try {
      await api.post("/b2b/vendedor/visitas", { negocio_id: showVisita.id, ...visita });
      toast.success("Visita registrada");
      setShowVisita(null); setVisita({ observaciones: "", proxima_visita: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setGuardando(false); }
  };

  const abrirPedido = async (n) => {
    setShowPedido(n); setCatalogo(null); setCantidades({}); setFechaEntrega("");
    try {
      const r = await api.get(`/b2b/vendedor/negocios/${n.id}/catalogo`);
      setCatalogo(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error cargando catálogo"); setShowPedido(null); }
  };

  const totales = () => {
    let total = 0, unidades = 0, margen = 0;
    (catalogo?.productos || []).forEach((p) => {
      const c = parseInt(cantidades[p.producto_id]) || 0;
      total += c * p.precio_mayorista; unidades += c; margen += c * p.margen_unitario;
    });
    return { total, unidades, margen };
  };

  const crearPedido = async () => {
    const items = Object.entries(cantidades).filter(([, c]) => parseInt(c) > 0).map(([producto_id, cantidad]) => ({ producto_id, cantidad: parseInt(cantidad) }));
    if (items.length === 0) return toast.error("Agrega cantidades al pedido");
    setGuardando(true);
    try {
      const r = await api.post(`/b2b/vendedor/negocios/${showPedido.id}/pedido`, { items, fecha_entrega: fechaEntrega || null });
      toast.success(`Pedido ${r.data.numero} creado para ${showPedido.nombre_comercial}`);
      setShowPedido(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error creando pedido"); } finally { setGuardando(false); }
  };

  const t = totales();

  return (
    <div className="space-y-4" data-testid="vendedor-negocios-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl text-torta-dark flex items-center gap-2"><Store className="w-6 h-6 text-torta-brown" /> Negocios TAT</h1>
          <p className="text-torta-muted text-sm">Registra establecimientos, visitas y pedidos mayoristas</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={copiarLink} data-testid="copiar-link-referido-btn"><Copy className="w-4 h-4 mr-1" /> Link de registro</Button>
          <Button size="sm" onClick={() => setShowRegistro(true)} className="bg-torta-brown hover:bg-torta-brown/90" data-testid="registrar-negocio-btn"><Plus className="w-4 h-4 mr-1" /> Nuevo negocio</Button>
        </div>
      </div>

      {loading ? <Loader2 className="w-7 h-7 animate-spin text-torta-brown mx-auto mt-10" /> : (
        <div className="space-y-3">
          {negocios.length === 0 && <p className="text-center text-sm text-torta-muted py-10">Aún no tienes negocios. ¡Registra el primero!</p>}
          {negocios.map((n) => (
            <div key={n.id} className="bg-white rounded-2xl p-4 shadow-sm border border-torta-sand" data-testid={`negocio-card-${n.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-bold text-torta-dark">{n.nombre_comercial}</p>
                  <p className="text-xs text-torta-muted">{n.tipo_establecimiento} · {n.barrio || n.ciudad} · {n.telefono}</p>
                </div>
                <Badge className={`border-0 text-[10px] ${ESTADO_BADGE[n.estado]}`}>{n.estado.replace("_", " ")}</Badge>
              </div>
              <div className="flex gap-4 mt-2 text-[11px] text-torta-muted flex-wrap">
                {n.ultimo_pedido && <span>Último pedido: {n.ultimo_pedido.numero} · {fmt(n.ultimo_pedido.total)}</span>}
                {n.ultima_visita?.proxima_visita && <span>Próx. visita: {n.ultima_visita.proxima_visita}</span>}
                {!n.ultimo_pedido && n.estado === "activo" && <span className="text-amber-700 font-semibold">Sin pedidos aún — ofrécele el Paquete Descubre la Abuela</span>}
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setShowVisita(n)} data-testid={`visita-btn-${n.id}`}><CalendarClock className="w-3.5 h-3.5 mr-1" /> Visita</Button>
                {(n.estado === "activo" || n.estado === "aprobado") && (
                  <Button size="sm" onClick={() => abrirPedido(n)} className="bg-[#C8902A] hover:bg-[#B07D1E] text-white" data-testid={`pedido-btn-${n.id}`}>
                    <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Crear pedido
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Registro */}
      <Dialog open={showRegistro} onOpenChange={setShowRegistro}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar negocio</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input data-testid="tat-nombre" placeholder="Nombre comercial *" value={form.nombre_comercial} onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })} />
            <Input data-testid="tat-responsable" placeholder="Responsable *" value={form.responsable} onChange={(e) => setForm({ ...form, responsable: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="tat-telefono" placeholder="Teléfono *" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
              <Input data-testid="tat-email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="tat-direccion" placeholder="Dirección *" value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} />
              <Input placeholder="Barrio" value={form.barrio} onChange={(e) => setForm({ ...form, barrio: e.target.value })} />
            </div>
            <select value={form.tipo_establecimiento} onChange={(e) => setForm({ ...form, tipo_establecimiento: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm">
              {["Tienda de barrio", "Minimercado", "Panadería", "Cafetería", "Restaurante", "Supermercado", "Otro"].map((x) => <option key={x}>{x}</option>)}
            </select>
            <Button data-testid="tat-guardar-btn" onClick={registrar} disabled={guardando} className="w-full bg-torta-brown hover:bg-torta-brown/90">
              {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Visita */}
      <Dialog open={!!showVisita} onOpenChange={(v) => !v && setShowVisita(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Visita a {showVisita?.nombre_comercial}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Textarea data-testid="visita-observaciones" rows={3} placeholder="Observaciones comerciales" value={visita.observaciones} onChange={(e) => setVisita({ ...visita, observaciones: e.target.value })} />
            <div>
              <p className="text-xs text-torta-muted mb-1">Próxima visita</p>
              <Input type="date" value={visita.proxima_visita} onChange={(e) => setVisita({ ...visita, proxima_visita: e.target.value })} />
            </div>
            <Button data-testid="visita-guardar-btn" onClick={registrarVisita} disabled={guardando} className="w-full bg-torta-brown hover:bg-torta-brown/90">
              {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ClipboardList className="w-4 h-4 mr-1" /> Registrar visita</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pedido */}
      <Dialog open={!!showPedido} onOpenChange={(v) => !v && setShowPedido(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Pedido para {showPedido?.nombre_comercial}</DialogTitle></DialogHeader>
          {!catalogo ? <Loader2 className="w-6 h-6 animate-spin text-torta-brown mx-auto my-6" /> : (
            <div className="space-y-2">
              <p className="text-xs text-torta-muted">Lista <b>{catalogo.lista.nombre}</b> ({catalogo.lista.margen_pct}% margen) · Mínimo reposición: {fmt(catalogo.reglas.minimo_valor)} o {catalogo.reglas.minimo_unidades} unidades (primer pedido exento) · Entrega con {catalogo.reglas.anticipacion_horas}h de anticipación</p>
              {catalogo.productos.map((p) => (
                <div key={p.producto_id} className="flex items-center gap-2 border border-torta-sand rounded-xl px-3 py-2" data-testid={`prod-row-${p.producto_id}`}>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-torta-dark">{p.nombre}</p>
                    <p className="text-[11px] text-torta-muted">Compra {fmt(p.precio_mayorista)} · Sugerido {fmt(p.precio_publico_sugerido)} · Gana {fmt(p.margen_unitario)}</p>
                  </div>
                  <Input type="number" min="0" className="w-16 h-8 text-center" value={cantidades[p.producto_id] || ""}
                    onChange={(e) => setCantidades({ ...cantidades, [p.producto_id]: e.target.value })} data-testid={`cant-input-${p.producto_id}`} />
                </div>
              ))}
              <div>
                <p className="text-xs text-torta-muted mb-1">Fecha de entrega solicitada (mín. {catalogo.reglas.anticipacion_horas}h)</p>
                <Input type="date" value={fechaEntrega} onChange={(e) => setFechaEntrega(e.target.value)} data-testid="pedido-fecha-entrega" />
              </div>
              <div className="bg-torta-cream rounded-xl p-3 text-sm flex justify-between" data-testid="pedido-totales">
                <span>{t.unidades} unidades</span>
                <span className="font-bold text-torta-dark">{fmt(t.total)} <span className="text-[10px] text-green-700 font-normal">(margen potencial {fmt(t.margen)})</span></span>
              </div>
              <Button data-testid="pedido-crear-btn" onClick={crearPedido} disabled={guardando} className="w-full bg-[#C8902A] hover:bg-[#B07D1E] text-white">
                {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar pedido"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
